import { formatDate } from '@/utils/dateUtils';
import { capitalizeFirstLetter } from '@/utils/stringUtils';
import React, { useContext, useEffect, useState } from 'react';
import { generateClient, GraphQLQuery } from 'aws-amplify/api';
import { ListOrganizationUsersFilteredQuery, OrganizationUserStatus } from '@/API';
import { listOrganizationUsersFilteredLite as listOrganizationUsersFiltered } from '@/graphql/queries.custom';
import { DeleteIcon, EditIcon } from '../Icons/CommonIcons';
import DeleteOrgUserModal from './DeleteOrgUserModal';
import { OrgUserContext } from './OrgUserContext';
import { OrgUserType } from './OrgUserType.model';
import UpdateOrgUserModal from './UpdateOrgUserModal';
import { AnimatePresence, motion } from 'motion/react';
import { resendUserInvite } from '@/client/organization';

const OrgUserList: React.FC = () => {
  const {
    isEdit,
    setShowModal,
    currentOrgUsers,
    setCurrentOrgUsers,
    currentOrgUser,
    setCurrentUser,
    usersNextToken,
    setUsersNextToken,
    currentOrganization
  } = useContext(OrgUserContext);
  const client = generateClient();
  const [loadingMore, setLoadingMore] = useState(false);

  const [isUpdateOrgUserOpen, setIsUpdateOrgUserOpen] = useState(false);
  const [isDeleteOrgUserOpen, setIsDeleteOrgUserOpen] = useState(false);
  const [resendingInvites, setResendingInvites] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isEdit) {
      setShowModal(false);
    }
  }, [isEdit, setShowModal]);

  const handleLoadMore = async () => {
    if (loadingMore || !usersNextToken) return;
    try {
      setLoadingMore(true);
      const orgId = currentOrganization?.id || currentOrgUsers[0]?.organizationID;
      const resp = await client.graphql<GraphQLQuery<ListOrganizationUsersFilteredQuery>>({
        query: listOrganizationUsersFiltered,
        variables: {
          filter: {
            organizationId: { eq: orgId },
            accountStatus: { ne: OrganizationUserStatus.DELETED }
          },
          limit: 10,
          nextToken: usersNextToken
        }
      });
      const conn = resp.data?.listOrganizationUsersFiltered as unknown as {
        items: OrgUserType[];
        nextToken?: string;
      };
      if (conn?.items?.length) {
        setCurrentOrgUsers([...currentOrgUsers, ...conn.items]);
        setUsersNextToken(conn.nextToken);
      } else {
        setUsersNextToken(undefined);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  const handleUpdateUser = (user: OrgUserType) => {
    setCurrentUser(user);
    setIsUpdateOrgUserOpen(true);
  };

  const handleDeleteUser = (user: OrgUserType) => {
    setCurrentUser(user);
    setIsDeleteOrgUserOpen(true);
  };

  const handleResendInvite = async (user: OrgUserType) => {
    try {
      setResendingInvites((prev) => new Set([...prev, user.id]));
      await resendUserInvite(user.email, user.role === 'ADMIN');
    } catch {
      setResendingInvites((prev) => {
        const newSet = new Set(prev);
        newSet.delete(user.id);
        return newSet;
      });
    }
  };
  if (!currentOrgUsers.length) {
    return <div className="text-center py-16">No Users for this Organization</div>;
  }

  return (
    <div className="w-full overflow-x-auto mt-16">
      <div className="grid grid-cols-8 gap-16 mb-16">
        <div className="px-8 col-span-2">Name</div>
        <div className="px-8 col-span-2">Email</div>
        <div className="px-8">Joined Date</div>
        <div className="px-8">Role</div>
        <div className="px-8">Status</div>
        <div className="px-8">Actions</div>
      </div>
      <div className="space-y-8">
        {currentOrgUsers.map((user: any) => (
          <div
            key={user.id}
            className="grid grid-cols-8 gap-16 bg-gray-100 p-16 items-center hover:bg-gray-200 rounded-3xl"
          >
            <div className="px-8 col-span-2 flex items-center truncate">
              <img
                src={`https://ui-avatars.com/api/?background=random&bold=true&format=svg&name=${user.username}`}
                alt="profile"
                className="w-32 h-32 rounded-full mr-8"
              />
              {user.username}
            </div>
            <a
              className="px-8 col-span-2 truncate text-[#41797b] hover:underline"
              href={`mailto:${user.email}`}
            >
              {user.email}
            </a>
            <div className="px-8">{user?.createdAt && formatDate(user.createdAt)}</div>
            <div className="px-8"> {capitalizeFirstLetter(user?.role)}</div>
            <div className="px-8 flex flex-col">
              <div className="flex items-center gap-8">
                <span
                  className={`inline-block w-8 h-8 rounded-full shrink-0 ${
                    user?.accountStatus === 'PENDING' ? 'bg-orange-500' : 'bg-green-500'
                  }`}
                ></span>
                {capitalizeFirstLetter(user?.accountStatus)}
              </div>
              {user?.accountStatus === 'PENDING' && (
                <AnimatePresence mode="wait">
                  {!resendingInvites.has(user.id) ? (
                    <motion.button
                      key="resend"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => handleResendInvite(user)}
                      className="text-left text-sm text-pink-600 hover:text-pink-800 underline mt-4"
                    >
                      Resend Invite
                    </motion.button>
                  ) : (
                    <motion.span
                      key="sent"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="text-sm text-green-600 mt-4"
                    >
                      Invite Sent!
                    </motion.span>
                  )}
                </AnimatePresence>
              )}
            </div>
            <div className="flex space-x-8">
              <button onClick={() => handleUpdateUser(user)} className="p-8">
                <EditIcon className="w-16 h-16 hover:scale-110 transition-transform" />
              </button>
              <button onClick={() => handleDeleteUser(user)} className="p-2">
                <DeleteIcon className="w-20 h-20 hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {usersNextToken && (
        <div className="mt-4 flex justify-center">
          <button
            className="btn btn-primary small mt-4"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load More'}
          </button>
        </div>
      )}

      <UpdateOrgUserModal
        isOpen={isUpdateOrgUserOpen}
        setIsOpen={setIsUpdateOrgUserOpen}
        currentOrgUser={currentOrgUser}
        setCurrentOrgUser={setCurrentUser}
        currentOrgUsers={currentOrgUsers}
        setCurrentOrgUsers={setCurrentOrgUsers}
      />
      <DeleteOrgUserModal
        isOpen={isDeleteOrgUserOpen}
        setIsOpen={setIsDeleteOrgUserOpen}
        currentOrgUser={currentOrgUser}
        setCurrentOrgUser={setCurrentUser}
      />
    </div>
  );
};

export default OrgUserList;
